import type { TourStep } from '@/components/ui/Tour';
import { ORGANIZATION_SETTINGS_TOUR_STEP_IDS, TOUR_CENTERED_STEP_SELECTOR } from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface OrganizationSettingsTourOptions {
  onEnterBasic?: () => void;
  onEnterPublicSite?: () => void;
  onEnterSports?: () => void;
  onEnterPlayerRatings?: () => void;
  /** Called when the user completes the final tour step (Finish). */
  onFinish?: () => void;
}

export function getOrganizationSettingsTourSteps(
  options: OrganizationSettingsTourOptions = {},
): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Organization settings"
          description="Manage contact details, your public site, sports offered, and internal player ratings for match play — all from one place."
        />
      ),
      selectorId: TOUR_CENTERED_STEP_SELECTOR,
    },
    {
      content: (
        <TourStepContent
          title="Settings tabs"
          description="Basic information covers contact details. Public site handles branding and discovery. Sports sets what you teach. Player ratings configures internal matchmaking scales."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.TABS,
      position: 'bottom',
      onStepEnter: options.onEnterBasic,
    },
    {
      content: (
        <TourStepContent
          title="Organization contact"
          description="Add description, address, phone, email, and country so families see accurate details on your public site footer and directory listing."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.BASIC,
      position: 'top',
      onStepEnter: options.onEnterBasic,
    },
    {
      content: (
        <TourStepContent
          title="Public site URL"
          description="Choose path or branded subdomain mode, set your public slug, and preview how families reach your organization page."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PUBLIC_SITE_URL,
      position: 'bottom',
      onStepEnter: options.onEnterPublicSite,
    },
    {
      content: (
        <TourStepContent
          title="Directory listing"
          description="Control whether your organization appears on the CourtSync public directory when you want more discovery."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PUBLIC_SITE_DIRECTORY,
      position: 'bottom',
      onStepEnter: options.onEnterPublicSite,
    },
    {
      content: (
        <TourStepContent
          title="Branding & colors"
          description="Set primary and secondary colors used on your white-label public organization page."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PUBLIC_SITE_BRANDING,
      position: 'bottom',
      onStepEnter: options.onEnterPublicSite,
    },
    {
      content: (
        <TourStepContent
          title="Logo & banner"
          description="Upload images families see at the top of your public site — logo for nav and a wide banner hero. Save when you are done — changes apply to your live public page."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PUBLIC_SITE_MEDIA,
      position: 'top',
      onStepEnter: options.onEnterPublicSite,
    },
    {
      content: (
        <TourStepContent
          title="Sports you offer"
          description="Turn on every racquet sport your organization teaches, choose one primary sport for defaults, then save so lessons, programs, and courts scope correctly."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.SPORTS_LIST,
      position: 'bottom',
      onStepEnter: options.onEnterSports,
    },
    {
      content: (
        <TourStepContent
          title="Player ratings"
          description="Configure internal rating scales for match play pairing and tournament seeding. Ratings stay operator-only — not shown on public boards or member portals."
        />
      ),
      selectorId: ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PLAYER_RATINGS_FORM,
      position: 'top',
      onStepEnter: options.onEnterPlayerRatings,
    },
  ];
}

export const organizationSettingsTourSteps: TourStep[] = getOrganizationSettingsTourSteps();
